/* What a salon has bought, worked out from the rows.
 *
 * The grants are a history — one row per grant, never edited into something
 * else, never deleted (see admin/server/schema/14-licensing.sql). So "what is
 * this salon entitled to" is not a column anywhere; it is a reading taken over
 * that history, and it is taken twice:
 *
 *   in SQL   `private.license_resolve()`, which is the AUTHORITY. It is what the
 *            console asks, so it is what actually decides whether a board draws.
 *   here     for the admin app, which has the whole history loaded anyway and
 *            has to label every row in it, not just the winner.
 *
 * Two implementations of one rule is a real cost and it is paid deliberately:
 * the alternative is a round trip per row to have the database describe rows the
 * browser is already holding. What keeps them honest is that the rule is three
 * lines long and stated identically in both places —
 *
 *     not revoked, started, most recent by starts_at, ties broken by created_at
 *
 * — and that the three state names below are the three the SQL returns. If that
 * ordering is ever changed, it is changed in both files or the admin app will
 * show a salon a licence its console is not honouring.
 *
 * Pure functions over plain rows. No Vue, no Supabase, so the boundaries — a
 * term that ends today, a grant that starts tomorrow — are testable without
 * standing either up.
 */

/* ---------------- the features ----------------
 *
 * A feature id is a string the CONSOLE looks for. There is no table of them and
 * no foreign key, because a feature exists when a build checks for it — the
 * database's opinion about which ids are real would be a second, staler answer.
 *
 * So this registry classifies rather than filters, exactly as
 * settings-keys.js does for setting keys: a known id gets a label and a
 * sentence about what switching it off actually does, and an unknown one is
 * shown as itself and marked. An admin looking at a plan needs to see what is
 * really in it, including a feature this build has never heard of — because the
 * reason to open the screen is usually that something is set to something odd.
 *
 * `bite` is not decoration. It is the answer to "what will they notice", which
 * is the only question anybody asks before taking a feature away.
 */
var FEATURES = [
  {
    id: 'turn-tracker',
    label: 'Turn Tracker',
    hint: 'The board itself — the module id in the console\'s js/apps.js.',
    bite: 'The board is replaced by a card saying it is not licensed. Nothing is deleted, and every turn comes back the moment it is granted again.'
  },
  {
    id: 'appointments',
    label: 'Appointments',
    hint: 'The scheduling plugin: the book, the appointments pane, checkout times.',
    bite: 'The console behaves as though no booking system were connected — no pane, no due-now row, no starting a turn off the book. The mirrored book itself is untouched.'
  },
  {
    id: 'channel',
    label: 'Booking channels',
    hint: 'An outside source of bookings that are not on the book — the watched mailbox.',
    bite: 'The channel slot switches off and stops polling. Reservations already matched stay where they are.'
  },
  {
    id: 'self-checkin',
    label: 'Self check-in',
    hint: 'The lobby tablet — checkin.html, its Settings tab, and the front desk\'s waiting list.',
    bite: 'The check-in page becomes a card saying it is not on their plan, and the desk\'s Waiting list goes with it. Anybody already on that list stays in the database and comes straight back. The board, the book and the front desk are untouched — this is the only feature that costs a salon a screen their CLIENTS see, so a lobby tablet left out will be showing an upgrade notice to the waiting area within a quarter of an hour.'
  },
  {
    id: 'reports',
    label: 'Reports',
    hint: 'The Reports module. Registered in the console, not built yet.',
    bite: 'It leaves the module switcher. Since it is a placeholder either way, this one costs a salon nothing today.'
  },
  {
    id: 'reviews',
    label: 'Reviews',
    hint: 'The Reviews module. Registered in the console, not built yet.',
    bite: 'It leaves the module switcher. Same as Reports — a placeholder for now.'
  },
  {
    id: 'dreamcloud',
    label: 'DreamCloud',
    hint: 'Health, numbers and fixes for the DreamCloud worker. Owner only.',
    bite: 'The DreamCloud module leaves the module switcher. Only the Owner role can hold it (16-owner-only-features.sql), so this costs the owner a screen and nobody else anything.'
  },
  {
    id: 'optimizer',
    label: 'Optimizer',
    hint: 'Proposes technician changes that open up online booking for scarce services, for the desk to approve.',
    bite: 'The module leaves the switcher, the scheduled check skips this salon, and the desk can no longer approve moves. Moves already made stay made, and the board and the book are untouched. Who takes a booked client is a separate setting and stays as it is.'
  }
];

var BY_ID = {};
FEATURES.forEach(function (f) { BY_ID[f.id] = f; });

function knownFeature(id) { return !!BY_ID[id]; }
function featureMeta(id) { return BY_ID[id] || null; }

// Sorted, de-duplicated, empties dropped — the same normalisation the SQL does
// with `array_agg(distinct f order by f)`, so a plan's features read the same
// in this app as they do in the database.
function normalise(list) {
  var seen = {};
  var out = [];
  (list || []).forEach(function (raw) {
    var id = String(raw == null ? '' : raw).trim();
    if (!id || seen[id]) return;
    seen[id] = true;
    out.push(id);
  });
  return out.sort();
}

/* What a grant entitles: the plan's features plus the grant's own.
 *
 * Additive only. A grant cannot take a feature away from its plan — see the
 * note on the column in 14-licensing.sql — because a plan that means something
 * different for every salon on it has stopped being a plan.
 */
function featuresOf(plan, grant) {
  return normalise((plan && plan.features ? plan.features : [])
    .concat((grant && grant.features) || []));
}

// Features on the grant that its plan does not already include. What the
// screen calls "extras", and the only part of `featuresOf` a person chose.
function extrasOf(plan, grant) {
  var inPlan = {};
  ((plan && plan.features) || []).forEach(function (f) { inPlan[f] = true; });
  return normalise(((grant && grant.features) || []).filter(function (f) {
    return !inPlan[f];
  }));
}

/* ---------------- the reading ---------------- */

function ms(value) { return value ? new Date(value).getTime() : null; }

/* The grant in force, or null.
 *
 * Character for character the rule in `private.license_resolve()`: not revoked,
 * already started, latest `starts_at` wins, `created_at` breaks a tie — which is
 * what a correction made in the same minute looks like.
 *
 * A future `starts_at` is not a mistake to be tolerated, it is the feature that
 * makes a renewal something you can set up before the term ends. Such a row sits
 * there entitling nothing until its moment.
 */
function inForce(rows, now) {
  var t = now || Date.now();
  var best = null;
  (rows || []).forEach(function (r) {
    if (r.revoked_at) return;
    var starts = ms(r.starts_at);
    if (starts == null || starts > t) return;
    if (!best) { best = r; return; }
    var a = ms(best.starts_at);
    if (starts > a) { best = r; return; }
    if (starts === a && ms(r.created_at) > ms(best.created_at)) best = r;
  });
  return best;
}

// The three states the database returns, and no fourth one. `expiring` below is
// a nuance OF active rather than a state beside it, so that a caller comparing
// against 'active' cannot be surprised by a salon whose licence is fine.
function stateOf(grant, now) {
  if (!grant) return 'none';
  var expires = ms(grant.expires_at);
  if (expires != null && expires <= (now || Date.now())) return 'lapsed';
  return 'active';
}

/* How long is left on a term, as something to render.
 *
 * Days rather than the minutes a support session counts, because that is the
 * unit the decision is made in — nobody renews a licence in the last quarter of
 * an hour, and a countdown that said "3h" would be describing an emergency
 * nobody can do anything about anyway.
 *
 * `soon` is a fortnight, which is chosen to be longer than a weekend and longer
 * than most people's idea of "later this week": it is the point at which the
 * tenant list should start saying something unprompted.
 */
var SOON_DAYS = 14;
var DAY_MS = 86400000;

function remaining(expiresAt, now) {
  if (!expiresAt) {
    return { perpetual: true, expired: false, days: null, label: 'no end date', soon: false };
  }
  var left = new Date(expiresAt).getTime() - (now || Date.now());
  if (!(left > 0)) {
    return { perpetual: false, expired: true, days: 0, label: 'expired', soon: true };
  }
  // Rounded UP: a term with eleven hours on it has a day left, not none. The
  // number is used to decide whether to warn, and rounding a warning away on
  // its last day is the one direction that cannot be recovered from.
  var days = Math.ceil(left / DAY_MS);
  var label;
  if (days <= 1) label = 'today';
  else if (days < 45) label = days + ' days';
  else if (days < 400) label = Math.round(days / 30) + ' months';
  else label = Math.round(days / 365) + ' years';
  return {
    perpetual: false, expired: false, days: days, label: label, soon: days <= SOON_DAYS
  };
}

/* Everything one screen needs about one salon's licence, in one object.
 *
 * `plans` is the catalogue keyed however the caller has it — an array or a map
 * both work, because a page that has just loaded the plans should not have to
 * reshape them to ask this.
 */
function planFrom(plans, id) {
  if (!id || !plans) return null;
  if (Array.isArray(plans)) {
    return plans.find(function (p) { return p.id === id; }) || null;
  }
  return plans[id] || null;
}

function entitlement(rows, plans, now) {
  var t = now || Date.now();
  var grant = inForce(rows, t);
  var plan = grant ? planFrom(plans, grant.plan_id) : null;
  var state = stateOf(grant, t);
  var live = state === 'active';
  var left = grant ? remaining(grant.expires_at, t) : remaining(null, t);

  return {
    grant: grant,
    plan: plan,
    planId: grant ? grant.plan_id : null,
    // A grant whose plan row has not loaded, or has been renamed away, still
    // names its plan — the id is on the grant itself and is the honest fallback.
    planName: (plan && plan.name) || (grant ? grant.plan_id : null),
    state: state,
    licensed: live,
    // Features are empty unless the licence is live. A lapsed licence entitles
    // nothing — that is what lapsed means — and the database says the same, so
    // a screen reading this cannot show a salon features its console has lost.
    features: live ? featuresOf(plan, grant) : [],
    extras: grant ? extrasOf(plan, grant) : [],
    seats: grant ? (grant.seats == null ? (plan ? plan.seats : null) : grant.seats) : null,
    startsAt: grant ? grant.starts_at : null,
    expiresAt: grant ? grant.expires_at : null,
    left: left,
    expiring: live && left.soon && !left.perpetual
  };
}

/* ---------------- the history ----------------
 *
 * What each row of the list is, relative to the one in force. Five answers,
 * because "not the current one" covers four situations that want different
 * sentences:
 *
 *   in force     this is the licence the console is honouring
 *   scheduled    its term has not started yet — a renewal set up early
 *   lapsed       it IS the one in force, and its term has ended
 *   superseded   a later grant replaced it. Nobody withdrew it; it was renewed
 *   revoked      somebody withdrew it, and that is a different fact
 */
function rowState(row, current, now) {
  var t = now || Date.now();
  if (!row) return 'none';
  if (row.revoked_at) return 'revoked';
  var starts = ms(row.starts_at);
  if (starts != null && starts > t) return 'scheduled';
  if (current && row.id === current.id) {
    return stateOf(row, t) === 'lapsed' ? 'lapsed' : 'in force';
  }
  return 'superseded';
}

// Which pill colour the state gets. Here rather than in the template because
// the same mapping is wanted in two places and a template with a condition in
// it is where these drift.
var STATE_KIND = {
  'in force': 'ok',
  scheduled: 'warn',
  lapsed: 'bad',
  revoked: 'bad',
  superseded: '',
  active: 'ok',
  none: 'bad'
};

function stateKind(state) {
  return STATE_KIND[state] == null ? '' : STATE_KIND[state];
}

/* ---------------- the grant form ---------------- */

// The terms the form offers. A month, a quarter, a year, and no end — which are
// the four a salon is actually sold. Anything else is typed as a date.
var TERMS = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: 'A year' },
  { days: null, label: 'No end date' }
];

/* When a term that starts at `startsAt` and runs `days` ends, as an ISO string.
 *
 * Null days is perpetual and returns null, which is what the column holds for
 * one — not a date a long way off. The two are different rows and only one of
 * them keeps being true.
 */
function termEnd(startsAt, days) {
  if (days == null) return null;
  var n = Math.round(Number(days));
  if (!isFinite(n) || n <= 0) return null;
  var from = startsAt ? new Date(startsAt).getTime() : Date.now();
  return new Date(from + n * DAY_MS).toISOString();
}

/* A renewal starts where the current term ends, not now.
 *
 * Renewing a licence with three weeks left and having those three weeks
 * evaporate is the mistake this exists to make impossible — it is invisible on
 * the screen that makes it and obvious on the invoice. A perpetual or already
 * lapsed licence has no end to run on from, so a renewal of one starts now.
 */
function renewalStart(grant, now) {
  var t = now || Date.now();
  var end = grant ? ms(grant.expires_at) : null;
  return new Date(end != null && end > t ? end : t).toISOString();
}

// Null is a real answer and means "no limit". Zero is not: a licence for no
// accounts is a licence nobody can sign in under, which is a suspension written
// in the wrong place, so it is refused rather than stored.
function cleanSeats(value) {
  if (value === '' || value == null) return null;
  var n = Math.round(Number(value));
  if (!isFinite(n) || n < 1) return undefined;
  return Math.min(n, 999);
}

/* Where a salon is against its seat limit.
 *
 * `over` can be true, and the app must not pretend otherwise: lowering a limit
 * below what a salon already has does not sign anybody out — the members are
 * still there and still work — it only stops the next one being attached. The
 * admin-api function enforces exactly that, and a screen that showed "6/5" as
 * an error rather than as a fact would be describing a failure that has not
 * happened.
 */
function seatState(seats, used) {
  var n = used || 0;
  if (seats == null) {
    return { limit: null, used: n, free: null, over: false, full: false,
             label: n + (n === 1 ? ' account' : ' accounts') + ', no limit' };
  }
  return {
    limit: seats,
    used: n,
    free: Math.max(0, seats - n),
    over: n > seats,
    full: n >= seats,
    label: n + ' of ' + seats + (seats === 1 ? ' seat' : ' seats')
  };
}

export const licensing = {
  FEATURES: FEATURES,
  TERMS: TERMS,
  SOON_DAYS: SOON_DAYS,
  knownFeature: knownFeature,
  featureMeta: featureMeta,
  normalise: normalise,
  featuresOf: featuresOf,
  extrasOf: extrasOf,
  inForce: inForce,
  stateOf: stateOf,
  remaining: remaining,
  entitlement: entitlement,
  rowState: rowState,
  stateKind: stateKind,
  termEnd: termEnd,
  renewalStart: renewalStart,
  cleanSeats: cleanSeats,
  seatState: seatState
};
